<template>
  <DsPolicyBlockedSkeleton v-if="loading" />

  <div class="w-full flex flex-col lg:flex-row justify-between items-start gap-4 mb-4 mt-4">
    <span
      @click="toggleInsert"
      class="flex items-center gap-2 cursor-pointer font-bold text-primary"
    >
      <v-icon class="share-icon" color="primary" :icon="mdiPlusCircle" size="32" />
      Inserir Médicos
    </span>
  </div>

  <div
    v-if="showInsert"
    class="w-full flex flex-col lg:flex-row justify-between items-start gap-4 mb-8"
  >
    <div class="w-full lg:w-4/5">
      <v-autocomplete
        @update:model-value="setUsers"
        @update:search="searchUsers"
        :items="doctorStore.listDoctorsCompany"
        :loading="loadingBloqued"
        clearable
        chips
        multiple
        class="w-full"
        color="primary20"
        label="Médicos"
        variant="outlined"
        density="comfortable"
      />
    </div>

    <div class="w-full lg:w-1/5">
      <filled-button
        @click="submitUsers"
        :loading="loadingBloqued"
        :text="`Inserir Médicos`"
        width="100%"
        height="56px"
        :disabled="loading"
      />
    </div>
  </div>

  <div class="w-full mt-4 overflow-y-auto overflow-x-hidden" style="grid-area: table">
    <template v-if="!loading">
      <template v-if="paginatedDoctors.length > 0">
        <div
          class="hidden md:grid lg:grid-cols-12 md:grid-cols-8 gap-2 w-full items-center text-left py-2 border-b font-semibold text-gray-700 text-xs md:text-sm"
        >
          <div class="md:col-span-3">Nome</div>
          <div class="md:col-span-1">CRM</div>
          <div class="md:col-span-1">CRM-UF</div>
          <div class="md:col-span-2">Data de Inserção</div>
          <div class="md:col-span-3">Inserido Por</div>
          <div class="md:col-span-2 flex">Ações</div>
        </div>

        <TableBlockedPolicies
          v-for="doc in paginatedDoctors"
          :key="doc.id"
          :data="{
            id: doc.id,
            name: doc.doctor.user.name,
            crm: doc.doctor.mainCrm.number,
            crmUf: doc.doctor.mainCrm.uf,
            insertedAt: formatDateTimeAtBR(doc.createdAt),
            insertedBy: doc.createdBy.name
          }"
        />
      </template>

      <EmptyList v-else title="Nenhum médico bloqueado encontrado" />
    </template>
  </div>

  <v-pagination
    v-if="blockedDoctors.length > 0"
    v-model="currentPage"
    :total-visible="5"
    :length="pageCount"
    density="comfortable"
    color="primary"
    @update:model-value="changePage"
  />
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

import { mdiPlusCircle } from '@mdi/js'
import { formatDateTimeAtBR } from '@/utils/date'
import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'
import { useDoctorStore, DOCTOR_FETCH_ALL_LIMIT } from '@/pinia-store/useDoctorStore'
import { useErrorToast } from '@/composables/useErrorToast'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import TableBlockedPolicies from '@/components/designSystem/Table/TableBlockedDsPolicies.vue'
import EmptyList from '@/components/EmptyList.vue'
import DsPolicyBlockedSkeleton from '@/components/designSystem/Skeleton/DsPolicyBlockedSkeleton.vue'

const { notifyError } = useErrorToast()
const loading = ref(false)

const currentPage = ref(1)
const itemsPerPage = ref(5)

const dsPolicyStore = useDsPolicyStore()
const doctorStore = useDoctorStore()

const { blockedDoctors } = storeToRefs(dsPolicyStore)
const { doctorsCompany } = storeToRefs(doctorStore)

const showInsert = ref(false)
const loadingBloqued = ref(false)
const selectedUsers = ref<string[]>([])

onMounted(async () => {
  loading.value = true
  try {
    doctorStore.doctorQuery.limit = DOCTOR_FETCH_ALL_LIMIT
    await doctorStore.fetchDoctors()
  } catch (err) {
    console.error('Erro ao carregar médicos:', err)
  }

  try {
    const policyId = dsPolicyStore.currentPolicy?.id
    if (policyId) await dsPolicyStore.fetchBlockedDoctorsByDsPolicy(policyId)
  } catch (err) {
    notifyError(err, 'Não foi possível carregar os médicos bloqueados da política.')
  } finally {
    loading.value = false
  }
})

const toggleInsert = () => {
  showInsert.value = !showInsert.value
}

const emit = defineEmits(['update:users'])

const setUsers = (v: string[]) => {
  selectedUsers.value = v
  emit('update:users', v)
}

const searchUsers = async (search: string) => {
  try {
    loadingBloqued.value = true
    await doctorStore.applyFilters({ search }, false)
    doctorsCompany.value = doctorStore.doctorsCompany
  } catch (error) {
    console.error('Erro ao buscar médicos:', error)
  } finally {
    loadingBloqued.value = false
  }
}

const submitUsers = async () => {
  const policyId = Number(dsPolicyStore.currentPolicy?.id)

  if (!policyId || isNaN(policyId)) {
    console.error('Policy ID inválido:', policyId)
    return
  }

  const doctorIds = selectedUsers.value.map(Number)

  const payload = {
    dsPolicyId: policyId,
    doctorIds
  }

  loading.value = true
  try {
    await dsPolicyStore.addBlockedDoctors(policyId, payload)
  } catch (err) {
    // Mantém a seleção e o formulário aberto para o usuário tentar de novo.
    notifyError(err, 'Não foi possível bloquear os médicos na política de restrição.')
    loading.value = false
    return
  }

  selectedUsers.value = []
  showInsert.value = false

  try {
    await dsPolicyStore.fetchBlockedDoctorsByDsPolicy(policyId)
  } catch (err) {
    notifyError(err, 'Não foi possível atualizar a lista de médicos bloqueados.')
  } finally {
    loading.value = false
  }
}

const paginatedDoctors = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return blockedDoctors.value.slice(start, end)
})

const pageCount = computed(() => Math.ceil(blockedDoctors.value.length / itemsPerPage.value))

const changePage = (page: number) => {
  currentPage.value = page
}
</script>
